<script setup>
import { defineProps, ref, computed } from "vue";
import ProfilDosen from "@/Components/Web/ProfilDosen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const props = defineProps({
  formasi: {
    type: Object,
    required: true,
  },
});

const items = computed(() => props.formasi?.data || []);

const isModalOpen = ref(false);
const viewFormasi = ref(null);
const isLoading = ref(false);

const fotoUrl = (author) => {
  const foto = author?.dosen?.foto;
  if (!foto) return null;
  return foto.startsWith("http") ? foto : "/storage/" + foto;
};

const viewProfile = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewFormasi.value = null;
  try {
    const response = await fetch(`/pejabat/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    const data = await response.json();
    viewFormasi.value = data.formasidata;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <section class="py-10 lg:py-12" v-if="items.length">
    <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
      <!-- Section header -->
      <div class="max-w-2xl mx-auto mb-10 text-center">
        <span
          class="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-semibold tracking-wide uppercase rounded-full text-primary bg-primary/10"
        >
          <span class="inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
          Profil
        </span>
        <h2
          class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl font-roboto-slab"
        >
          Pimpinan
        </h2>
        <p class="mt-3 text-base leading-relaxed text-gray-500">
          Susunan pimpinan dan struktur organisasi.
        </p>
      </div>

      <!-- Cards (centered, wraps for any count) -->
      <div class="flex flex-wrap justify-center gap-6">
        <button
          v-for="(author, index) in items"
          :key="author.id || index"
          type="button"
          @click="viewProfile(author.id)"
          class="flex flex-col w-40 overflow-hidden text-center transition-all duration-300 bg-white border border-gray-100 shadow-sm sm:w-52 lg:w-60 group rounded-2xl hover:shadow-xl hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <!-- Photo -->
          <div class="relative overflow-hidden bg-gray-100 aspect-[4/5]">
            <img
              v-if="fotoUrl(author)"
              :src="fotoUrl(author)"
              :alt="author.dosen?.nama"
              class="absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
            />
            <div
              v-else
              class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5"
            >
              <i class="fa-solid fa-user text-6xl text-primary/40"></i>
            </div>
            <span
              class="absolute inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white transition-opacity duration-300 rounded-full opacity-0 top-3 right-3 bg-primary/90 backdrop-blur-sm group-hover:opacity-100"
            >
              <i class="fa-solid fa-eye"></i>
              Lihat
            </span>
          </div>

          <!-- Content -->
          <div class="flex flex-col flex-1 p-4">
            <h3
              class="text-sm font-semibold leading-snug text-gray-900 transition-colors sm:text-base group-hover:text-primary font-roboto-slab line-clamp-2"
            >
              {{ author.dosen?.nama }}
            </h3>
            <div class="w-10 h-1 mx-auto my-2 rounded-full bg-primary"></div>
            <p class="text-xs text-gray-500 sm:text-sm line-clamp-2">
              {{ author.jabatan?.namajabatan }}
            </p>
          </div>
        </button>
      </div>

      <!-- Modal -->
      <Loading v-if="isLoading" :active="isLoading" :is-full-page="true" />
      <ProfilDosen
        v-if="isModalOpen && !isLoading"
        v-model:isOpen="isModalOpen"
        :dosendata="viewFormasi?.dosen"
        :title="`${viewFormasi?.jabatan?.namajabatan}`"
        style="position: fixed; z-index: 9999; left: 0; top: 0"
      />
    </div>
  </section>
</template>
